// createSchema — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-schema' })` op (DSE2 §3.D6).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D5 — Schema UI.
//
// Per-engine surface (per DSE2 dialect.schema):
//   - mssql:  CREATE SCHEMA name [AUTHORIZATION user]
//             (no IF NOT EXISTS — T-SQL grammar lacks the keyword)
//   - pgsql:  CREATE SCHEMA [IF NOT EXISTS] name [AUTHORIZATION role]
//   - mysql:  unsupported — schemas == databases (use slice G1).
//   - oracle: unsupported — schemas == users (use slice F1).
//   - sqlite: unsupported — sqlite has no DDL schemas.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, SchemaDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateSchemaArgs {
  readonly schema: SchemaDef;
}

export function buildCreateSchemaEdit(args: CreateSchemaArgs): DdlEdit {
  return { op: 'create-schema', schema: args.schema };
}

export async function commitCreateSchema(
  ctx: VqeDdlEditContext,
  args: CreateSchemaArgs,
): Promise<void> {
  const edit = buildCreateSchemaEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const SCHEMA_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres',
] as const);

export function isCreateSchemaSupported(engine: SqlEngine): boolean {
  return SCHEMA_SUPPORTED.has(engine);
}

export function getCreateSchemaUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL conflates schemas with databases. Use CREATE / ALTER / DROP '
      + 'DATABASE instead.'
    );
  }
  if (engine === 'oracle') {
    return (
      'Oracle conflates schemas with users. Create a user — the '
      + 'schema is owned by and named after that user.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no DDL schemas. Use ATTACH DATABASE to bring in another '
      + 'database file when logical separation is needed.'
    );
  }
  return '';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateSchemaForEngine(
  engine: SqlEngine,
  args: CreateSchemaArgs,
): string | null {
  if (!isCreateSchemaSupported(engine)) {
    return getCreateSchemaUnsupportedTooltip(engine);
  }
  const s = args.schema;
  if (s.name.trim().length === 0) {
    return 'Schema name is required.';
  }
  if (engine === 'mssql') {
    if (s.ifNotExists) {
      return (
        'mssql does not support IF NOT EXISTS on CREATE SCHEMA. Wrap the '
        + 'statement in an IF SCHEMA_ID(\'name\') IS NULL guard if conditional '
        + 'creation is needed.'
      );
    }
    if (s.pgsqlAuthorization !== undefined) {
      return 'mssql expects mssqlAuthorization, not pgsqlAuthorization.';
    }
  }
  if (engine === 'postgres') {
    if (s.mssqlAuthorization !== undefined) {
      return 'postgres expects pgsqlAuthorization, not mssqlAuthorization.';
    }
  }
  return null;
}
